import { useState, useEffect, useCallback, useRef } from 'react';


function useTimeout(durationInSeconds, onTimerEndCallback) {
    const [timeLeft, setTimeLeft] = useState(durationInSeconds);
    const [isPaused, setIsPaused] = useState(false);
    const [isActive, setIsActive] = useState(false);

    const timerIdRef = useRef(null); // To store the timeout ID

    // The function to call when timer ends
    const handleTimerEnd = useCallback(() => {
        setIsActive(false);
        if (onTimerEndCallback) {
            onTimerEndCallback();
        }
    }, [onTimerEndCallback]);

    useEffect(() => {
        if (!isActive || isPaused || timeLeft <= 0) {
            clearTimeout(timerIdRef.current);
            return;
        }

        timerIdRef.current = setTimeout(() => {
            setTimeLeft(prevTime => prevTime - 1);
            if (timeLeft - 1 <= 0) {
                handleTimerEnd();
            }
        }, 1000);

        return () => clearTimeout(timerIdRef.current); // Cleanup on unmount or re-render
    }, [timeLeft, isPaused, isActive, handleTimerEnd]);

    const start = useCallback(() => { // Could be an explicit start, or auto-start
        setTimeLeft(durationInSeconds);
        setIsPaused(false);
        setIsActive(true);
    }, [durationInSeconds]);

    const pause = useCallback(() => {
        setIsPaused(true);
    }, []);

    const resume = useCallback(() => {
        setIsPaused(false);
    }, []);

    const reset = useCallback(() => {
        clearTimeout(timerIdRef.current);
        setTimeLeft(durationInSeconds);
        setIsPaused(false);
        setIsActive(false); 
        start();
    }, [durationInSeconds]);

    const startTimer = useCallback(() => {
        clearTimeout(timerIdRef.current);
        setTimeLeft(durationInSeconds);
        setIsPaused(false);
        setIsActive(true);
    }, [durationInSeconds]);

    return {
    timeoutTimeLeft:timeLeft,
    timeoutIsPaused:isPaused,
    timeoutIsActive:isActive,
    timeoutPause: pause,
    timeoutResume: resume,
    timeoutReset: reset, 
    timeoutStart: startTimer 
  };
}

export default useTimeout;